import gsap, { Power3 } from 'gsap';
import { Group } from 'three';
import { Maths } from '../_app/cuchillo/utils/Maths';
import { isMobile } from '../_app/cuchillo/core/Basics';

import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import ThumbnailObject from './ThumbnailObject';
import { GetBy } from '../_app/cuchillo/core/Element';
import Scene from './Scene';

// Llums exportades des de Scene.js — re-export per compatibilitat amb imports existents
export { PRODUCT_LIGHTS_OBSCURIOS, PRODUCT_LIGHTS_LUMINARIS } from './Scene';

const GRID_INTRO_Z = 4000;
const GRID_EXIT_Z = 4000;
const GRID_EXIT_START = 0.5;
const GRID_EXIT_STAGGER = 0.05;

export default class MoreProductsScene {

  static group = new Group();
  static _categ = null;
  static _tempGrids = [];
  static _tempContainers = null;
  static _container = null;
  static active = false;

  static init(container) {

    if (!WebGLSketch.renderer) WebGLSketch.init();

    WebGLSketch.scene.add(this.group);

    const thumbsContainer = GetBy.class('__product-thumbnails', container)[0];
    const categ = thumbsContainer ? thumbsContainer.dataset.categ : null;
    this._categ = categ;
    this._container = container;

    Scene.initProductLights(categ);

    // La creació dels thumbnails (GLB+textura+material per cadascun) es diferix fins
    // showTempGrid() — veure comentari allà i el mateix fix a ProductScene._initGridFor.
  }

  static _initMoreProductsThumbnails(container) {

    this._tempGrids = [];
    this._tempContainers = null;

    const containerThumbs = GetBy.class('block-more-products', container)[0];

    const items = [];
    const thumbEls = GetBy.class('__thumbnail', containerThumbs);

    for (let i = 0; i < thumbEls.length; i++) {

      const el = thumbEls[i];

      const img = new ThumbnailObject({
        dom: el,
        link: el.dataset.link || '',
        hasMouse: true,
        zFactor: 0.08,
      });

      img._introZ = GRID_INTRO_Z;
      img._exitZ = 0;
      img.setOpacity(0);

      img.init().then(() => Scene.setProductLayer(img, this._categ));

      this.group.add(img);

      this._tempGrids.push(img);
      this._tempContainers = containerThumbs;
    }
  }

  static showTempGrid() {
    this.animateIn();

    // Creació diferida: abans es feia a static init() (a l'inici de la càrrega de la
    // pàgina, encara que el grid fos avall de tot). Ara, just quan el block és a punt
    // d'entrar en pantalla (cridat des de ScrollItem__BlockProductThumbnails.onShow).
    if (!this._tempContainers) this._initMoreProductsThumbnails(this._container);

    const items = this._tempGrids || [];
    const stagger = isMobile ? 0.04 : 0.08;
    const duration = 2.5;

    items.forEach((img, i) => {

      gsap.killTweensOf(img);
      gsap.killTweensOf(img._op);

      gsap.to(img._op, {
        value: 1,
        duration: .5,
        delay: .2 + i * stagger
      });

      gsap.to(img, {
        _introZ: 0,
        duration,
        delay: i * stagger,
        ease: Power3.easeOut
      });
    });
  }

  static moveTempGrid(progress) {

    const items = this._tempGrids || [];

    items.forEach((img, i) => {

      const start = GRID_EXIT_START + i * GRID_EXIT_STAGGER;

      img._exitZ =
        Maths.clamp(
          Maths.map(progress, start, 1, 0, 1),
          0,
          1
        ) * GRID_EXIT_Z;
    });
  }

  static loop() {

    if (!this.active) return;

    this._tempGrids.forEach(img => {

      if (!img.mesh) return;

      const { x, y, width, height } = img.dom.getBoundingClientRect();

      if (width === 0 || height === 0) return;

      const pos = img.domPositionTo3D(x, y);

      img.pos.x = pos.x + width * .5;
      img.pos.y = pos.y - height * .5;
      img.pos.z = img.dataZ + (img._introZ || 0) + (img._exitZ || 0);

      img.update();
    });
  }

  static animateIn() {
    this.active = true;
  }

  static hide() {
    this.active = false;
  }

  static resize() {
    this._tempGrids.forEach(img => img.resize());
  }

  static dispose() {

    Scene.disposeProductLights();

    this._tempGrids.forEach(img => {

      gsap.killTweensOf(img);
      gsap.killTweensOf(img._op);

      this.group.remove(img);
      img.dispose();
    });

    this._tempGrids = [];
    this._tempContainers = null;

    WebGLSketch.scene.remove(this.group);
  }
}